// API Client · Sickinger Terminliste
// Cloudflare Access setzt Auth-Cookie automatisch, wir brauchen nur fetch() mit credentials.

const API_BASE = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_BASE ?? '');

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message: string) {
    super(message);
  }
}

// Alle Fehlermeldungen für Benutzer auf Deutsch: Server-Texte (meist schon deutsch)
// übernehmen, technische Codes und HTTP-Status übersetzen.
const FEHLER_CODES: Record<string, string> = {
  gate_required: 'Bitte zuerst im Sickinger-Portal anmelden.',
  rate_limited: 'Zu viele Versuche – bitte kurz warten und erneut versuchen.',
  not_found: 'Nicht gefunden.',
  'not found': 'Nicht gefunden.',
  'bad json': 'Ungültige Anfrage.',
  confirm_required: 'Bitte die Aktion bestätigen.',
  key_required: 'Schlüssel erforderlich.',
  admin_password_required: 'Admin-Passwort erforderlich.',
  admin_not_configured: 'Admin-Zugang ist nicht eingerichtet.',
  bookmark_or_timestamp_required: 'Bitte einen Zeitpunkt oder eine Sicherung auswählen.',
  'unbekannte tabelle': 'Unbekannte Tabelle.',
};
const FEHLER_STATUS: Record<number, string> = {
  400: 'Ungültige Eingabe.',
  401: 'Nicht angemeldet – bitte über das Sickinger-Portal anmelden.',
  403: 'Keine Berechtigung.',
  404: 'Nicht gefunden.',
  409: 'Konflikt – bitte Seite neu laden und erneut versuchen.',
  413: 'Die Daten sind zu groß.',
  429: 'Zu viele Versuche – bitte kurz warten und erneut versuchen.',
};
function fehlerMeldung(status: number, body: unknown): string {
  const b = (body && typeof body === 'object' ? body : {}) as { error?: unknown; message?: unknown };
  for (const t of [b.message, b.error]) {
    if (typeof t !== 'string' || !t.trim()) continue;
    const code = FEHLER_CODES[t.trim().toLowerCase()];
    if (code) return code;
    if (!/^[a-z_]+$/.test(t.trim())) return t.trim();   // lesbarer (deutscher) Server-Text
  }
  return FEHLER_STATUS[status] ?? (status >= 500 ? 'Serverfehler – bitte später erneut versuchen.' : `Unerwarteter Fehler (Code ${status}).`);
}

// Für alert()/Fehleranzeigen: immer ein deutscher, verständlicher Text
export function fehlerText(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error && /[äöüß]|bitte|fehler|nicht/i.test(e.message)) return e.message;   // eigene deutsche Meldung
  // Programmteil nicht ladbar – meist, weil seit dem Öffnen der Seite ein Update eingespielt wurde
  if (e instanceof Error && /dynamically imported module|Importing a module script failed|module script|MIME type|reading 'jsPDF'/i.test(e.message)) {
    return 'Die Seite wurde inzwischen aktualisiert. Bitte mit Strg+F5 neu laden und erneut versuchen.';
  }
  console.error(e);
  return 'Unerwarteter Fehler – bitte die Seite mit Strg+F5 neu laden und erneut versuchen.';
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        // Für lokale Entwicklung: Dev-User setzen
        ...(import.meta.env.DEV && { 'X-Dev-User': 'hamdi@sickinger.de' }),
        ...options.headers
      }
    });
  } catch {
    throw new ApiError(0, null, 'Keine Verbindung zum Server – bitte Internetverbindung prüfen und erneut versuchen.');
  }

  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* ignore */ }
    throw new ApiError(res.status, body, fehlerMeldung(res.status, body));
  }
  return res.json() as Promise<T>;
}

// === Types (shared with API) ===
export interface CurrentSession {
  id: number;
  user_name: string | null;
  abteilung_id: number | null;
  abteilung_name: string | null;
  abteilung_farbe: string | null;
  started_at: string;
  running_minutes: number;
}

export interface Drueckteil {
  id: number;
  bestellnr: string;
  dv_nummer: string | null;
  position: string | null;
  sachbearbeiter_id: number | null;
  sachbearbeiter_name?: string;
  masse_bezeichnung: string | null;
  material: string | null;
  stueck: number;
  kunde_id: number | null;
  kunde_name?: string;
  lieferdatum: string;
  lieferdatum_minus3: string;
  kw: number;
  jahr: number;
  zeit_pro_stueck: number | null;
  gesamtzeit: number;
  baugruppenname: string | null;
  teile_geschnitten: string | null;
  kommentar: string | null;
  priority: 'normal' | 'eilig' | 'kritisch';
  fertigungsdatum: string | null;
  gefertigt_von_id: number | null;
  fertig: number;
  stand_id: number | null;
  stand_wert?: string;
  stand_farbe?: string;
  vorkasse: number;
  vorkasse_bezahlt_am: string | null;
  created_at: string;
  updated_at: string;
  current_session?: string | null;  // JSON string, parse edilecek
}

// === Beistelteile (Phase 1 MVP) ===
export type BeistelteilStatus = 'erwartet'|'eingegangen'|'verbaut'|'versendet'|'fehlt'|'beschaedigt';
export type BeistelteilZustand = 'ok'|'beschaedigt'|'unklar';
export type BeistelteilModul = 'drueckteile'|'laufraeder'|'baugruppen'|'montage'|'laser_kant';

export interface Beistelteil {
  id: number;
  auftrag_modul: BeistelteilModul;
  auftrag_id: number;
  bezeichnung: string;
  zeichnungs_nr: string | null;
  erwartete_stueckzahl: number;
  erwartet_bis: string | null;
  status: BeistelteilStatus;
  eingang_datum: string | null;
  eingang_user_id: number | null;
  eingang_user_name?: string | null;
  ist_stueckzahl: number | null;
  lagerplatz: string | null;
  zustand: BeistelteilZustand | null;
  notes: string | null;
  verbaut_datum: string | null;
  verbaut_user_id: number | null;
  verbaut_user_name?: string | null;
  created_at: string;
  updated_at: string;
}

export interface WareneingangUebersicht {
  erwartet_woche: number;
  heute_eingegangen: number;
  ueberfaellig: number;
}

export interface WareneingangListe {
  ueberfaellig: Beistelteil[];
  erwartet: Beistelteil[];
  heute_eingegangen: Beistelteil[];
}

export interface BeistelteilSummary {
  total: number;
  erwartet: number;
  eingegangen: number;
  verbaut: number;
  versendet: number;
  ueberfaellig: number;
}

export interface Kunde { id: number; name: string }
export interface User  {
  id: number; name: string; kurzel: string | null; role: string;
  abteilung_id?: number | null; abteilung_name?: string | null; abteilung_farbe?: string | null;
  ticket_bereiche?: string | null;
}
export interface StandWert { id: number; wert: string; farbe: string; sort_order: number; is_final: number }
// „Neue Bestellungen“ für den Einkauf (Menü-Badge, NEU-Etiketten, Popup)
export interface EinkaufNeuAufgabe { id: number; titel: string; typ: string; prio: string; created_at: string; von: string | null }
export interface EinkaufNeuBanfPos {
  id: number; zettel_id: number; dv_nummer: string; zettel: string; bezeichnung: string;
  menge: number; einheit: string | null; created_at: string; von: string | null;
}
export interface EinkaufNeuBedarf {
  id: number; form: string | null; werkstoff: string | null; format: string | null; dicke: string | null; menge: string | null;
  referenz: string | null; sb: string | null; priority: string; typ: string; created_at: string;
}
export interface EinkaufNeuSonstiges {
  id: number; kategorie: string | null; stueck: string | null; was: string | null; referenz: string | null; created_at: string; von: string | null;
}
export interface EinkaufNeuDaten {
  aktiv: boolean; vorname: string | null; aufgaben: EinkaufNeuAufgabe[]; banf: EinkaufNeuBanfPos[]; bedarf: EinkaufNeuBedarf[];
  sonstiges: EinkaufNeuSonstiges[];
}

export interface TicketNeuEintrag {
  id: number; titel: string; typ: 'fehler' | 'wunsch' | 'frage'; prio: 'normal' | 'wichtig' | 'dringend';
  bereich: string | null; created_at: string; von: string | null;
}
/** Änderung an einem Ticket, das ICH gemeldet habe (Statuswechsel, Zuweisung, Nachricht) */
export interface TicketMelderHinweis {
  id: number; titel: string; status: string; art: 'status' | 'zuweisung' | 'kommentar' | string;
  text: string | null; von: string | null; am: string;
}
export interface TicketNeuDaten {
  aktiv: boolean; vorname: string | null; tickets: TicketNeuEintrag[];
  melder?: TicketMelderHinweis[]; bearbeiter?: boolean;
}

export interface Abteilung { id: number; name: string; farbe: string; sort_order: number }

export interface ArbeitSession {
  id: number;
  user_id: number;
  user_name?: string;
  user_kurzel?: string;
  module_table: 'drueckteile' | 'laufraeder' | 'baugruppen' | 'montage';
  record_id: number;
  abteilung_id: number | null;
  abteilung_name?: string | null;
  abteilung_farbe?: string | null;
  started_at: string;
  ended_at: string | null;
  running_minutes?: number;
  duration_minutes?: number | null;
  notes?: string | null;
  blocker_reason?: string | null;
  auftrag?: {
    bestellnr: string;
    dv_nummer: string | null;
    stueck: number;
    bezeichnung: string | null;
    lieferdatum: string;
    priority: string;
    kunde_name: string | null;
  } | null;
}

export interface FeedEvent {
  event: 'session_start' | 'session_end' | 'fertig' | 'stand_change';
  event_id: number;
  at: string;
  user_name: string | null;
  kurzel: string | null;
  modul: string;
  record_id: number;
  abteilung_name: string | null;
  abteilung_farbe: string | null;
  bestellnr?: string | null;
  kunde_name?: string | null;
  bezeichnung?: string | null;
  new_stand_wert?: string | null;
  new_stand_farbe?: string | null;
}

export interface HeuteItem {
  modul: string;
  label: string;
  soll: number;
  ist: number;
  rest: number;
  ueberfaellig: number;
  soll_stunden: number;
  ist_stunden: number;
  rest_stunden: number;
  ueberfaellig_stunden: number;
}
export interface HeuteResponse {
  data: HeuteItem[];
  total: {
    soll: number; ist: number; rest: number; ueberfaellig: number;
    soll_stunden: number; ist_stunden: number; rest_stunden: number; ueberfaellig_stunden: number;
  };
}
export interface HeuteModuleDetail {
  soll: number;
  ist: number;
  rest: number;
  ueberfaellig: number;
  offene_stunden: number | null;
}

export type ProblemKategorie = 'material'|'masse'|'zeichnung'|'oberflaeche'|'maschine'|'sonstige';
export type ProblemStatus = 'offen'|'in_bearbeitung'|'geloest'|'abgelehnt';
export type ProblemPriority = 'normal'|'hoch'|'kritisch';

export interface ProblemMeldung {
  id: number;
  module_table: 'drueckteile'|'laufraeder'|'baugruppen'|'montage';
  record_id: number;
  gemeldet_von_user_id: number;
  gemeldet_von_name: string | null;
  gemeldet_von_kurzel: string | null;
  abteilung_id: number | null;
  abteilung_name: string | null;
  abteilung_farbe: string | null;
  kategorie: ProblemKategorie;
  beschreibung: string | null;
  priority: ProblemPriority;
  status: ProblemStatus;
  assigned_to_user_id: number | null;
  assigned_to_name: string | null;
  loesung: string | null;
  gemeldet_am: string;
  bearbeitet_am: string | null;
  geloest_am: string | null;
  auftrag?: {
    bestellnr: string;
    dv_nummer: string | null;
    bezeichnung: string | null;
    kunde_name: string | null;
    lieferdatum?: string;
    stueck?: number;
  } | null;
}

export interface ProblemCount {
  offen: number;
  in_bearbeitung: number;
  heute_geloest: number;
  heute_neu: number;
  kritisch: number;
}

export interface HeatmapEntry {
  id: number;
  name: string;
  farbe: string;
  sort_order: number;
  aktive_sessions: number;
  aktive_users: number;
  heute_fertig_sessions: number;
  heute_aktiv: number;
  jemals_bearbeitet: number;
  offene_auftraege: number;
  main_module: string | null;
}

export interface Paginated<T> {
  data: T[];
  pagination: { total: number; limit: number; offset: number; hasMore: boolean };
}

// === Gasbestellung (Kraiss & Friz) ===
// zusatz: in der App gelistet, im PDF als freie Zeile gedruckt (Formular-Vorlage bleibt unverändert)
export interface GasArtikel { artnr: string; artikel: string; groesse: string; flaschen?: number; zusatz?: boolean }
// Bestand je Artikel: Flaschen (voll + leer) in Werk 1 / Werk 2, davon leer
export type GasBestandWert = { w1?: string; w2?: string; leer?: string };
export interface GasPosition extends GasArtikel { menge: string; frei?: boolean; bestand?: GasBestandWert }
export interface GasLieferant {
  name: string; abteilung: string; strasse: string; ort: string; telefon: string; telefax: string;
  email: string; kundennr: string; lieferturnus: string;
}
export interface GasBesteller {
  firma: string; ansprechpartner: string; email: string; telefon: string;
  rechnungsadresse: string; lieferadresse: string;
}
export interface GasEinstellungen { lieferant: GasLieferant; besteller: GasBesteller; vertrag: { maxFlaschen: number }; artikel: GasArtikel[] }
export interface GasBestellung {
  id: number;
  bestelldatum: string; wunschtermin: string; bestelltext: string;
  ansprechpartner: string; email: string; telefon: string;
  rechnungsadresse: string; lieferadresse: string;
  positionen: GasPosition[]; leergut: string; leergut_detail: Record<string, number> | null;
  bestand: Record<string, GasBestandWert>; flaschen_nach: number | null;
  status: GasStatus; gesendet_am: string | null;
  uebergeben_am?: string | null; uebergeben_von?: string | null;
  geliefert_am?: string | null; geliefert_von?: string | null; leergut_abgeholt?: Record<string, number> | null;
  ohne_bestand?: number | boolean;   // Ausnahme: ohne Bestand gesendet (300er-Prüfung entfiel)
  created_at: string; erstellt_von?: string | null;
}

export type GasStatus = 'entwurf' | 'uebergeben' | 'gesendet' | 'geliefert';
export interface GasInventur {
  id: number; datum: string; bestand: Record<string, GasBestandWert>; summe: number; summe_w1: number; summe_w2: number; summe_leer: number;
  notiz: string | null; quelle: 'inventur' | 'bestellung'; bestellung_id: number | null; created_at: string; von: string | null;
}
// Flaschenkonto: letzte Zählung + Lieferungen − abgeholtes Leergut
export interface GasKonto {
  aktuell: number | null; max: number; inventur: GasInventur | null; inventuren: GasInventur[];
  bewegungen: { id: number; geliefert_am: string; plus: number; minus: number }[];
  plus: number; minus: number; tage_seit_zaehlung: number | null;
}
export interface GasStatistik {
  monate: number; ab: string | null;
  bestellungen: number; flaschen: number; abstand_tage: number | null;
  letzte: { id: number; bestelldatum: string; positionen: GasPosition[] } | null;
  artikel: { artnr: string; artikel: string; groesse: string; menge: number; anzahl: number; zuletzt: string; zuletzt_menge: number }[];
  monatsverlauf: { monat: string; flaschen: number; bestellungen: number }[];
}

// === Endpoints ===
export const api = {
  gas: {
    einstellungen: () => request<{ data: GasEinstellungen }>('/api/gas/einstellungen'),
    einstellungenSpeichern: (body: GasEinstellungen) =>
      request<{ ok: boolean }>('/api/gas/einstellungen', { method: 'PUT', body: JSON.stringify(body) }),
    list: () => request<{ data: GasBestellung[] }>('/api/gas'),
    statistik: (monate: number) => request<{ data: GasStatistik }>(`/api/gas/statistik?monate=${monate}`),
    create: (body: Partial<GasBestellung>) =>
      request<{ data: GasBestellung }>('/api/gas', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: number, body: Partial<GasBestellung>) =>
      request<{ data: GasBestellung }>(`/api/gas/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    remove: (id: number) => request<{ data: { id: number } }>(`/api/gas/${id}`, { method: 'DELETE' }),
    konto: () => request<{ data: GasKonto }>('/api/gas/konto'),
    inventur: (body: { datum?: string; bestand: Record<string, GasBestandWert>; notiz?: string }) =>
      request<{ data: { id: number } }>('/api/gas/inventur', { method: 'POST', body: JSON.stringify(body) }),
    inventurLoeschen: (id: number) => request<{ ok: boolean }>(`/api/gas/inventur/${id}`, { method: 'DELETE' }),
  },
  tagesziel: {
    get: () => request<{ data: Array<{
      abteilung_id: number; abteilung_name: string; abteilung_farbe: string;
      engpass_anzahl: number | null; engpass_typ: string | null;
      tageskapazitaet: number; ist_stunden: number; ist_count: number;
      auslastung_percent: number | null;
    }> }>('/api/sessions/tagesziel'),
  },
  sessions: {
    start: (body: { module_table: string; record_id: number; user_id: number; abteilung_id?: number | null }) =>
      request<{ data: ArbeitSession }>('/api/sessions/start', { method: 'POST', body: JSON.stringify(body) }),
    end: (id: number, body: { notes?: string; blocker_reason?: string } = {}) =>
      request<{ data: ArbeitSession }>(`/api/sessions/${id}/end`, { method: 'POST', body: JSON.stringify(body) }),
    active: () => request<{ data: ArbeitSession[] }>('/api/sessions/active'),
    feed:   (params: { limit?: number; modul?: string; abteilung_id?: number } = {}) => {
      const q = new URLSearchParams();
      if (params.limit)    q.set('limit', String(params.limit));
      if (params.modul)    q.set('modul', params.modul);
      if (params.abteilung_id) q.set('abteilung_id', String(params.abteilung_id));
      return request<{ data: FeedEvent[] }>(`/api/sessions/feed?${q}`);
    },
    heatmap: () => request<{ data: HeatmapEntry[] }>('/api/sessions/heatmap'),
    abteilungAuftraege: (id: number) => request<{
      data: Array<{
        modul: string; id: number; bestellnr: string; dv_nummer: string | null;
        position: string | null; stueck: number; bezeichnung: string | null;
        lieferdatum: string; kw: number; priority: string; gesamtzeit: number;
        stand_id: number | null; stand_wert: string | null; stand_farbe: string | null;
        updated_at: string; kunde_name: string | null; sachbearbeiter_name: string | null;
        fertig: number; is_active: number | null;
      }>;
      abteilung: { name: string; farbe: string };
    }>(`/api/sessions/abteilung/${id}/auftraege`),
    heute:   () => request<HeuteResponse>('/api/sessions/heute'),
    heuteModule: (modul: string) => request<{ data: HeuteModuleDetail }>(`/api/sessions/heute/${modul}`),
    record:  (modul: string, id: number) =>
      request<{ data: ArbeitSession[]; total_minutes: number }>(`/api/sessions/record/${modul}/${id}`),
  },
  problems: {
    list: (params: { status?: string; abteilung_id?: number; modul?: string; limit?: number } = {}) => {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) if (v !== undefined) q.set(k, String(v));
      return request<{ data: ProblemMeldung[] }>(`/api/problems?${q}`);
    },
    get:    (id: number) => request<{ data: ProblemMeldung }>(`/api/problems/${id}`),
    create: (body: Partial<ProblemMeldung> & {
      module_table: string; record_id: number; kategorie: ProblemKategorie;
      user_id?: number; abteilung_id?: number | null; priority?: ProblemPriority;
      beschreibung?: string;
    }) => request<{ data: ProblemMeldung }>('/api/problems', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: number, body: Partial<ProblemMeldung>) =>
      request<{ data: ProblemMeldung }>(`/api/problems/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    count:  () => request<{ data: ProblemCount }>('/api/problems/count'),
    record: (modul: string, id: number) =>
      request<{ data: ProblemMeldung[] }>(`/api/problems/record/${modul}/${id}`),
  },
  drueckteile: {
    list: (params: Record<string, string | number | boolean | undefined> = {}) => {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
      }
      return request<Paginated<Drueckteil>>(`/api/drueckteile?${q}`);
    },
    get:    (id: number) => request<{ data: Drueckteil }>(`/api/drueckteile/${id}`),
    create: (body: Partial<Drueckteil>) =>
      request<{ data: Drueckteil }>('/api/drueckteile', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: number, body: Partial<Drueckteil>) =>
      request<{ data: Drueckteil }>(`/api/drueckteile/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (id: number) =>
      request<{ data: { id: number; deleted: boolean } }>(`/api/drueckteile/${id}`, { method: 'DELETE' }),
    history: (id: number) => request<{ data: unknown[] }>(`/api/drueckteile/${id}/history`),
  },
  modul: {
    // Generic create für Laufräder / Baugruppen / Montage
    create: (modul: 'laufraeder' | 'baugruppen' | 'montage' | 'laser_kant', body: Record<string, unknown>) =>
      request<{ data: { id: number } }>(`/api/${modul}`, {
        method: 'POST', body: JSON.stringify(body),
      }),
  },
  lookup: {
    kunden:     () => request<{ data: Kunde[] }>('/api/lookup/kunden'),
    users:      () => request<{ data: User[] }>('/api/lookup/users'),
    standWerte: () => request<{ data: StandWert[] }>('/api/lookup/stand-werte'),
    abteilungen: () => request<{ data: Abteilung[] }>('/api/lookup/abteilungen'),
    updateUser: (id: number, body: { abteilung_id?: number | null; role?: string; name?: string; kurzel?: string; active?: boolean; ticket_bereiche?: string | null }) =>
      request<{ data: User }>(`/api/lookup/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  },
  dashboard: {
    me:            () => request<DashboardMe>('/api/dashboard/me'),
    meineAufgaben: () => request<{ data: Drueckteil[] }>('/api/dashboard/meine-aufgaben'),
    live:          () => request<DashboardLiveResponse>('/api/dashboard/live'),
    wochenrecap:   () => request<WochenRecap>('/api/dashboard/wochenrecap'),
  },
  statistik: {
    durchlaufzeitOverview: (days = 90) =>
      request<DurchlaufzeitOverview>(`/api/statistik/durchlaufzeit/overview?days=${days}`),
    durchlaufzeitByAuftrag: (modul: string, limit = 100, cleanOnly = false) =>
      request<{ modul: string; clean_only: boolean; data: DurchlaufzeitAuftrag[] }>(
        `/api/statistik/durchlaufzeit/by-auftrag?modul=${modul}&limit=${limit}&clean=${cleanOnly}`
      ),
    topLaufer: (limit = 10) =>
      request<{ data: TopLauferAuftrag[] }>(`/api/statistik/durchlaufzeit/top-laufer?limit=${limit}`),
  },
  kapazitaet: {
    overview: (jahr: number, kwStart: number, kwCount = 8) =>
      request<KapazitaetOverview>(`/api/kapazitaet/overview?jahr=${jahr}&kw_start=${kwStart}&kw_count=${kwCount}`),
    cell: (abteilungId: number, jahr: number, kw: number) =>
      request<{ data: KapazitaetZelle }>(`/api/kapazitaet/${abteilungId}/${jahr}/${kw}`),
    upsert: (abteilungId: number, jahr: number, kw: number, body: { stunden_soll: number; notes?: string | null; user_id?: number }) =>
      request<{ data: unknown }>(`/api/kapazitaet/${abteilungId}/${jahr}/${kw}`, {
        method: 'PUT', body: JSON.stringify(body),
      }),
    thisWeekOverload: () =>
      request<{ jahr: number; data: KapazitaetOverload[]; overload_count: number }>(`/api/kapazitaet/this-week-overload`),
  },
  planung: {
    abteilung: (id: number, limit = 20) =>
      request<{ data: PriorisierterAuftrag[]; abteilung: { name: string; farbe: string } }>(
        `/api/planung/abteilung/${id}?limit=${limit}`
      ),
  },
  beistelteile: {
    listForAuftrag: (modul: BeistelteilModul, auftrag_id: number) => {
      const q = new URLSearchParams({ auftrag_modul: modul, auftrag_id: String(auftrag_id) });
      return request<{ data: Beistelteil[] }>(`/api/beistelteile?${q}`);
    },
    create: (body: {
      auftrag_modul: BeistelteilModul; auftrag_id: number;
      bezeichnung: string; zeichnungs_nr?: string | null;
      erwartete_stueckzahl?: number; erwartet_bis?: string | null;
      notes?: string | null;
    }) => request<{ data: Beistelteil }>('/api/beistelteile', {
      method: 'POST', body: JSON.stringify(body),
    }),
    update: (id: number, body: Partial<Beistelteil> & { user_id?: number }) =>
      request<{ data: Beistelteil }>(`/api/beistelteile/${id}`, {
        method: 'PATCH', body: JSON.stringify(body),
      }),
    delete: (id: number) =>
      request<{ data: { id: number; deleted: boolean } }>(`/api/beistelteile/${id}`, {
        method: 'DELETE',
      }),
    history: (id: number) => request<{ data: unknown[] }>(`/api/beistelteile/${id}/history`),
    summary: (modul: BeistelteilModul, auftrag_ids: number[]) =>
      request<{ data: Record<string, BeistelteilSummary> }>('/api/beistelteile/summary', {
        method: 'POST', body: JSON.stringify({ auftrag_modul: modul, auftrag_ids }),
      }),
  },
  wareneingang: {
    uebersicht: () => request<{ data: WareneingangUebersicht }>('/api/wareneingang/uebersicht'),
    liste:      () => request<{ data: WareneingangListe }>('/api/wareneingang/liste'),
  },
  artikel: {
    list: (params: { q?: string; kat?: string; wg?: string; sort?: 'zugriffe' | 'name'; aktiv?: '1' | '0' | 'alle'; limit?: number; offset?: number } = {}) => {
      const sp = new URLSearchParams();
      if (params.q)     sp.set('q', params.q);
      if (params.kat)   sp.set('kat', params.kat);
      if (params.wg)    sp.set('wg', params.wg);
      if (params.sort)  sp.set('sort', params.sort);
      if (params.aktiv) sp.set('aktiv', params.aktiv);
      if (params.limit) sp.set('limit', String(params.limit));
      if (params.offset) sp.set('offset', String(params.offset));
      return request<{ data: Artikel[]; total?: number }>(`/api/artikel?${sp.toString()}`);
    },
    gruppen: (aktiv: '1' | '0' = '1') =>
      request<{ data: { name: string; n: number; zugriffe: number }[] }>(`/api/artikel/gruppen?aktiv=${aktiv}`),
    create: (body: Partial<Artikel> & { kategorie?: string }) =>
      request<{ data: Artikel; existing: boolean }>('/api/artikel', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: number, body: Partial<Artikel>) =>
      request<{ data: Artikel }>(`/api/artikel/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  },
  sammelzettel: {
    list: (status = 'offen', q = '') =>
      request<{ data: Sammelzettel[] }>(`/api/sammelzettel?status=${encodeURIComponent(status)}&q=${encodeURIComponent(q)}`),
    create: (body: SammelzettelNeu) =>
      request<{ data: Sammelzettel }>('/api/sammelzettel', { method: 'POST', body: JSON.stringify(body) }),
    get: (id: number) =>
      request<{ data: Sammelzettel; positionen: SammelzettelPos[]; darfEntscheiden?: boolean }>(`/api/sammelzettel/${id}`),
    update: (id: number, body: Partial<SammelzettelNeu>) =>
      request<{ data: Sammelzettel }>(`/api/sammelzettel/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    storno: (id: number, grund: string) =>
      request<{ data: Sammelzettel }>(`/api/sammelzettel/${id}/storno`, { method: 'POST', body: JSON.stringify({ grund }) }),
    addPos: (id: number, body: SammelzettelPosNeu) =>
      request<{ data: SammelzettelPos }>(`/api/sammelzettel/${id}/pos`, { method: 'POST', body: JSON.stringify(body) }),
    updatePos: (posId: number, body: Partial<SammelzettelPosNeu> & { status?: SammelzettelPos['status']; liefertermin?: string }) =>
      request<{ data: SammelzettelPos }>(`/api/sammelzettel/pos/${posId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    bestellen: (id: number, gruppen: SammelGruppe[]) =>
      request<SammelzettelErgebnis>(`/api/sammelzettel/${id}/bestellen`, { method: 'POST', body: JSON.stringify({ gruppen }) }),
    ausBedarf: (id: number, ids: number[]) =>
      request<{ data: SammelzettelPos[]; anzahl: number }>(`/api/sammelzettel/${id}/aus-bedarf`, { method: 'POST', body: JSON.stringify({ ids }) }),
    history: (id: number) => request<{ data: BedarfHistorie[] }>(`/api/sammelzettel/${id}/history`),
    rueckfragen: () => request<{ data: SammelzettelRueckfrage[]; darfEntscheiden: boolean }>('/api/sammelzettel/rueckfragen'),
    frage: (posId: number, text: string) =>
      request<{ data: SammelzettelPos }>(`/api/sammelzettel/pos/${posId}/rueckfrage`, { method: 'POST', body: JSON.stringify({ text }) }),
    frageZurueck: (posId: number) =>
      request<{ data: SammelzettelPos }>(`/api/sammelzettel/pos/${posId}/rueckfrage-zurueck`, { method: 'POST', body: '{}' }),
    entscheid: (posId: number, entscheid: 'freigeben' | 'ablehnen', text = '') =>
      request<{ data: SammelzettelPos }>(`/api/sammelzettel/pos/${posId}/entscheid`, { method: 'POST', body: JSON.stringify({ entscheid, text }) }),
  },
  blechbedarf: {
    list: (params: { status?: string; q?: string; limit?: number; offset?: number; kat?: string; typ?: string; werkstoff?: string; dicke?: string; format?: string; lieferant?: string; ohne_angefragt?: boolean } = {}) => {
      const sp = new URLSearchParams();
      if (params.status)    sp.set('status', params.status);
      if (params.ohne_angefragt) sp.set('ohne_angefragt', '1');
      if (params.kat)       sp.set('kat', params.kat);
      if (params.typ)       sp.set('typ', params.typ);
      if (params.werkstoff) sp.set('werkstoff', params.werkstoff);
      if (params.dicke)     sp.set('dicke', params.dicke);
      if (params.format)    sp.set('format', params.format);
      if (params.lieferant) sp.set('lieferant', params.lieferant);
      if (params.q)         sp.set('q', params.q);
      if (params.limit)     sp.set('limit', String(params.limit));
      if (params.offset)    sp.set('offset', String(params.offset));
      return request<{ data: Blechbedarf[]; pagination: { total: number; limit: number; offset: number; hasMore: boolean } }>(
        `/api/blechbedarf?${sp.toString()}`);
    },
    // Diagramme der Liste: Dicke × Format (gleiche Filter wie list, aber ohne dicke/format)
    matrix: (params: { status?: string; q?: string; kat?: string; typ?: string; werkstoff?: string; lieferant?: string; ohne_angefragt?: boolean } = {}) => {
      const sp = new URLSearchParams();
      if (params.status)    sp.set('status', params.status);
      if (params.ohne_angefragt) sp.set('ohne_angefragt', '1');
      if (params.kat)       sp.set('kat', params.kat);
      if (params.typ)       sp.set('typ', params.typ);
      if (params.werkstoff) sp.set('werkstoff', params.werkstoff);
      if (params.lieferant) sp.set('lieferant', params.lieferant);
      if (params.q)         sp.set('q', params.q);
      return request<BedarfMatrix>(`/api/blechbedarf/matrix?${sp.toString()}`);
    },
    create: (body: Partial<Blechbedarf>) =>
      request<{ data: Blechbedarf }>('/api/blechbedarf', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: number, body: Partial<Blechbedarf>) =>
      request<{ data: Blechbedarf }>(`/api/blechbedarf/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    // Kein Löschen mehr — nur Storno mit Grund; Wiederherstellung nur Admin/Meister.
    storno: (id: number, grund: string) =>
      request<{ ok: boolean }>(`/api/blechbedarf/${id}/storno`, { method: 'POST', body: JSON.stringify({ grund }) }),
    papierkorb: (params: { kat?: string; q?: string } = {}) => {
      const sp = new URLSearchParams();
      if (params.kat) sp.set('kat', params.kat);
      if (params.q)   sp.set('q', params.q);
      return request<{ data: BedarfPapierkorb[] }>(`/api/blechbedarf/papierkorb?${sp.toString()}`);
    },
    restore: (id: number) =>
      request<{ data: Blechbedarf }>(`/api/blechbedarf/${id}/restore`, { method: 'POST' }),
    dv: (q: string, exakt = false) =>
      request<BedarfDvUebersicht>(`/api/blechbedarf/dv?q=${encodeURIComponent(q)}${exakt ? '&exakt=1' : ''}`),
    dvOffen: () => request<{ data: DvOffen[] }>('/api/blechbedarf/dv-offen'),
    sammelbestellung: (gruppen: SammelGruppe[]) =>
      request<SammelErgebnis>('/api/blechbedarf/sammelbestellung', { method: 'POST', body: JSON.stringify({ gruppen }) }),
    bulkImport: (rows: Partial<Blechbedarf>[]) =>
      request<{ ok: boolean; inserted: number }>('/api/blechbedarf/import', { method: 'POST', body: JSON.stringify({ rows }) }),
  },
  einkauf: {
    stats: () => request<EinkaufStats>('/api/blechbedarf/stats'),
    werLiefertWas: () => request<{ data: WerLiefertWas[] }>('/api/lieferanten/wer-liefert-was'),
    werLiefertWasAnlegen: (body: Partial<WerLiefertWas>) =>
      request<{ data: WerLiefertWas }>('/api/lieferanten/wer-liefert-was', { method: 'POST', body: JSON.stringify(body) }),
    werLiefertWasLoeschen: (id: number) =>
      request<{ ok: boolean }>(`/api/lieferanten/wer-liefert-was/${id}`, { method: 'DELETE' }),
    werkstoffe: () => request<{ data: { werkstoff: string; n: number }[] }>('/api/blechbedarf/werkstoffe'),
    optionen: () => request<{ werkstoffe: string[]; formate: string[]; dicken: string[]; formen: string[]; lieferanten: string[]; lieferantenGenutzt?: { name: string; n: number }[]; werkstoffKurz: Record<string, string> }>('/api/blechbedarf/optionen'),
    analyse: (werkstoff: string) =>
      request<WerkstoffAnalyse>(`/api/blechbedarf/analyse?werkstoff=${encodeURIComponent(werkstoff)}`),
    angebote: (bedarfId: number) =>
      request<{ data: EinkaufAngebot[] }>(`/api/blechbedarf/${bedarfId}/angebote`),
    angebotAnlegen: (bedarfId: number, body: Partial<EinkaufAngebot>) =>
      request<{ data: EinkaufAngebot }>(`/api/blechbedarf/${bedarfId}/angebote`, { method: 'POST', body: JSON.stringify(body) }),
    angebotUpdate: (angebotId: number, body: Partial<EinkaufAngebot>) =>
      request<{ data: EinkaufAngebot }>(`/api/blechbedarf/angebote/${angebotId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    angebotLoeschen: (angebotId: number) =>
      request<{ ok: boolean }>(`/api/blechbedarf/angebote/${angebotId}`, { method: 'DELETE' }),
    bestellen: (bedarfId: number, angebotId: number) =>
      request<{ data: Blechbedarf }>(`/api/blechbedarf/${bedarfId}/bestellen`, { method: 'POST', body: JSON.stringify({ angebot_id: angebotId }) }),
    dateien: (bedarfId: number) =>
      request<{ data: BedarfDatei[] }>(`/api/blechbedarf/${bedarfId}/dateien`),
    dateiUrl: (bedarfId: number, name: string) =>
      `${API_BASE}/api/blechbedarf/${bedarfId}/dateien/${encodeURIComponent(name)}`,
    // Zeugnis-PDFs je Position (für die Zeugnis-Spalte der Liste)
    zeugnisPdfs: (ids: number[]) =>
      request<{ data: Record<number, string[]> }>(`/api/blechbedarf/zeugnis-pdfs?ids=${ids.join(',')}`),
    dateiHochladen: async (bedarfId: number, datei: File, typ?: string): Promise<{ data: BedarfDatei }> => {
      const fd = new FormData();
      fd.append('datei', datei);
      if (typ) fd.append('typ', typ);
      const res = await fetch(`${API_BASE}/api/blechbedarf/${bedarfId}/dateien`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
        headers: { ...(import.meta.env.DEV && { 'X-Dev-User': 'hamdi@sickinger.de' }) },
      });
      if (!res.ok) {
        let body: unknown = null;
        try { body = await res.json(); } catch { /* ignore */ }
        throw new ApiError(res.status, body, `API ${res.status}: Datei-Upload`);
      }
      return res.json() as Promise<{ data: BedarfDatei }>;
    },
    dateiLoeschen: (bedarfId: number, name: string) =>
      request<{ ok: boolean }>(`/api/blechbedarf/${bedarfId}/dateien/${encodeURIComponent(name)}`, { method: 'DELETE' }),
    history: (id: number) => request<{ data: BedarfHistorie[] }>(`/api/blechbedarf/${id}/history`),
    preise: (params: { werkstoff?: string; dicke?: string; format?: string }) => {
      const sp = new URLSearchParams();
      if (params.werkstoff) sp.set('werkstoff', params.werkstoff);
      if (params.dicke)     sp.set('dicke', params.dicke);
      if (params.format)    sp.set('format', params.format);
      return request<{ data: { werkstoff: string; format: string | null; dicke: string | null;
        letzter_preis: number; preis_einheit: string | null; lieferant: string | null;
        letztes_datum: string | null; anzahl: number }[] }>(`/api/blechbedarf/preise?${sp}`);
    },
  },
  lieferanten: {
    list: () => request<{ data: Lieferant[] }>('/api/lieferanten'),
    bewertung: () => request<{ data: LieferantBewertung[] }>('/api/lieferanten/bewertung'),
    create: (body: Partial<Lieferant>) =>
      request<{ data: Lieferant }>('/api/lieferanten', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: number, body: Partial<Lieferant>) =>
      request<{ data: Lieferant }>(`/api/lieferanten/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    remove: (id: number) =>
      request<{ ok: boolean }>(`/api/lieferanten/${id}`, { method: 'DELETE' }),
  },
  kunden: {
    verwaltung: () => request<{ data: KundeStamm[] }>('/api/lookup/kunden-verwaltung'),
    update: (id: number, body: Partial<Pick<KundeStamm, 'contact' | 'email' | 'phone' | 'notes'>>) =>
      request<{ data: KundeStamm }>(`/api/lookup/kunden/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  },
  kundeninfo: {
    get: () => request<{ ueberfaellig: KundenInfoTeil[]; sammel: KundenInfoTeil[][] }>('/api/kundeninfo'),
  },
  orderKommentare: {
    list: (modul: string, recordId: number) =>
      request<{ data: { id: number; text: string; created_at: string; user_name: string | null }[] }>(
        `/api/order-kommentare?modul=${modul}&record_id=${recordId}`),
    create: (modul: string, recordId: number, text: string) =>
      request<{ data: { id: number; text: string; created_at: string } }>('/api/order-kommentare', {
        method: 'POST', body: JSON.stringify({ modul, record_id: recordId, text }),
      }),
    remove: (id: number) => request<{ ok: boolean }>(`/api/order-kommentare/${id}`, { method: 'DELETE' }),
  },
  einkaufNeu: {
    list: () => request<EinkaufNeuDaten>('/api/einkauf-neu'),
    gesehen: (art: 'aufgabe' | 'banf' | 'bedarf' | 'sonstiges', ids: number[]) =>
      request<{ ok: boolean }>('/api/einkauf-neu/gesehen', { method: 'POST', body: JSON.stringify({ art, ids }) }),
  },
  ticketNeu: {
    list: () => request<TicketNeuDaten>('/api/ticket-neu'),
    gesehen: (ids: number[]) =>
      request<{ ok: boolean }>('/api/ticket-neu/gesehen', { method: 'POST', body: JSON.stringify({ ids }) }),
    gesehenMelder: (ids: number[]) =>
      request<{ ok: boolean }>('/api/ticket-neu/gesehen-melder', { method: 'POST', body: JSON.stringify({ ids }) }),
  },
  einkaufAufgaben: {
    list: (alle = false, sort?: 'neu') => {
      const sp = new URLSearchParams();
      if (alle) sp.set('alle', '1');
      if (sort) sp.set('sort', sort);
      const qs = sp.toString();
      return request<{ data: EinkaufAufgabe[] }>(`/api/einkauf-aufgaben${qs ? `?${qs}` : ''}`);
    },
    create: (body: { titel: string; notiz?: string; angefordert_von?: string; prio?: string; typ?: string; ansprechpartner?: string }) =>
      request<{ data: EinkaufAufgabe }>('/api/einkauf-aufgaben', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: number, body: Partial<Pick<EinkaufAufgabe, 'status' | 'titel' | 'notiz' | 'prio' | 'angefordert_von' | 'typ' | 'ansprechpartner'>>) =>
      request<{ data: EinkaufAufgabe }>(`/api/einkauf-aufgaben/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    storno: (id: number, grund: string) =>
      request<{ ok: boolean }>(`/api/einkauf-aufgaben/${id}/storno`, { method: 'POST', body: JSON.stringify({ grund }) }),
    papierkorb: () => request<{ data: EinkaufAufgabePapierkorb[] }>('/api/einkauf-aufgaben/papierkorb'),
    restore: (id: number) => request<{ data: EinkaufAufgabe }>(`/api/einkauf-aufgaben/${id}/restore`, { method: 'POST' }),
    history: (id: number) => request<{ data: BedarfHistorie[] }>(`/api/einkauf-aufgaben/${id}/history`),
    bildUrl: (id: number) => `${API_BASE}/api/einkauf-aufgaben/${id}/bild`,
    bildHochladen: async (id: number, datei: File): Promise<{ data: EinkaufAufgabe }> => {
      const fd = new FormData();
      fd.append('bild', datei);
      const res = await fetch(`${API_BASE}/api/einkauf-aufgaben/${id}/bild`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
        headers: { ...(import.meta.env.DEV && { 'X-Dev-User': 'hamdi@sickinger.de' }) },
      });
      if (!res.ok) {
        let body: unknown = null;
        try { body = await res.json(); } catch { /* ignore */ }
        throw new ApiError(res.status, body, `API ${res.status}: Bild-Upload`);
      }
      return res.json() as Promise<{ data: EinkaufAufgabe }>;
    },
  },
  sonderteile: {
    list: () => request<{ data: SonderteilAnfrage[] }>('/api/sonderteile'),
    create: (body: Partial<SonderteilAnfrage> & { was: string }) =>
      request<{ data: SonderteilAnfrage }>('/api/sonderteile', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: number, body: Partial<SonderteilAnfrage>) =>
      request<{ data: SonderteilAnfrage }>(`/api/sonderteile/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    bestellen: (id: number) =>
      request<{ data: SonderteilAnfrage }>(`/api/sonderteile/${id}/bestellen`, { method: 'POST' }),
    remove: (id: number) => request<{ ok: boolean }>(`/api/sonderteile/${id}`, { method: 'DELETE' }),
    dateien: (id: number) => request<{ data: TicketDatei[] }>(`/api/sonderteile/${id}/dateien`),
    dateiUrl: (id: number, name: string) => `${API_BASE}/api/sonderteile/${id}/dateien/${encodeURIComponent(name)}`,
    dateiHochladen: async (id: number, datei: File): Promise<{ data: TicketDatei }> => {
      const fd = new FormData();
      fd.append('datei', datei);
      const res = await fetch(`${API_BASE}/api/sonderteile/${id}/dateien`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
        headers: { ...(import.meta.env.DEV && { 'X-Dev-User': 'hamdi@sickinger.de' }) },
      });
      if (!res.ok) {
        let body: unknown = null;
        try { body = await res.json(); } catch { /* ignore */ }
        throw new ApiError(res.status, body, `API ${res.status}: Datei-Upload`);
      }
      return res.json() as Promise<{ data: TicketDatei }>;
    },
    dateiLoeschen: (id: number, name: string) =>
      request<{ ok: boolean }>(`/api/sonderteile/${id}/dateien/${encodeURIComponent(name)}`, { method: 'DELETE' }),
  },
  einkaufSonstiges: {
    list: (kategorie: EinkaufSonstigesKategorie, q?: string) => {
      const sp = new URLSearchParams({ kategorie });
      if (q) sp.set('q', q);
      return request<{ data: EinkaufSonstigesEintrag[] }>(`/api/einkauf-sonstiges?${sp}`);
    },
    create: (body: Partial<EinkaufSonstigesEintrag> & { kategorie: EinkaufSonstigesKategorie; was: string }) =>
      request<{ data: EinkaufSonstigesEintrag }>('/api/einkauf-sonstiges', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: number, body: Partial<EinkaufSonstigesEintrag>) =>
      request<{ data: EinkaufSonstigesEintrag }>(`/api/einkauf-sonstiges/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    storno: (id: number, grund: string) =>
      request<{ ok: boolean }>(`/api/einkauf-sonstiges/${id}/storno`, { method: 'POST', body: JSON.stringify({ grund }) }),
    papierkorb: (kategorie?: EinkaufSonstigesKategorie) =>
      request<{ data: EinkaufSonstigesPapierkorb[] }>(`/api/einkauf-sonstiges/papierkorb${kategorie ? `?kategorie=${kategorie}` : ''}`),
    restore: (id: number) => request<{ data: EinkaufSonstigesEintrag }>(`/api/einkauf-sonstiges/${id}/restore`, { method: 'POST' }),
    history: (id: number) => request<{ data: BedarfHistorie[] }>(`/api/einkauf-sonstiges/${id}/history`),
    bilderIndex: () => request<{ data: Record<string, number> }>('/api/einkauf-sonstiges/bilder-index'),
    bilder: (id: number) => request<{ data: SonstigesBild[] }>(`/api/einkauf-sonstiges/${id}/bilder`),
    bildUrl: (id: number, name: string) => `${API_BASE}/api/einkauf-sonstiges/${id}/bilder/${encodeURIComponent(name)}`,
    bilderHochladen: async (id: number, dateien: File[]): Promise<{ data: string[] }> => {
      const fd = new FormData();
      for (const f of dateien) fd.append('bild', f);
      const res = await fetch(`${API_BASE}/api/einkauf-sonstiges/${id}/bilder`, {
        method: 'POST', credentials: 'include', body: fd,
        headers: { ...(import.meta.env.DEV && { 'X-Dev-User': 'hamdi@sickinger.de' }) },
      });
      if (!res.ok) {
        let body: unknown = null;
        try { body = await res.json(); } catch { /* ignore */ }
        throw new ApiError(res.status, body, (body as { error?: string } | null)?.error ?? `API ${res.status}: Bild-Upload`);
      }
      return res.json() as Promise<{ data: string[] }>;
    },
    bildLoeschen: (id: number, name: string) =>
      request<{ ok: boolean }>(`/api/einkauf-sonstiges/${id}/bilder/${encodeURIComponent(name)}`, { method: 'DELETE' }),
  },
  tickets: {
    list: (status = 'offen', bereich = '', mine = false) =>
      request<{ data: Ticket[]; bearbeiter?: boolean }>(`/api/tickets?status=${status}${bereich ? `&bereich=${encodeURIComponent(bereich)}` : ''}${mine ? '&mine=1' : ''}`),
    count: () => request<{ offen: number }>('/api/tickets/count'),
    create: (body: { titel: string; beschreibung?: string; bereich?: string; prio?: string; typ?: string }) =>
      request<{ data: Ticket }>('/api/tickets', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: number, body: Partial<Pick<Ticket, 'status' | 'prio' | 'typ' | 'titel' | 'beschreibung' | 'bereich' | 'zugewiesen_an'>> & { grund?: string; nachricht?: string }) =>
      request<{ data: Ticket }>(`/api/tickets/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    uebernehmen: (id: number) =>
      request<{ data: Ticket }>(`/api/tickets/${id}/uebernehmen`, { method: 'POST' }),
    remove: (id: number) => request<{ ok: boolean }>(`/api/tickets/${id}`, { method: 'DELETE' }),
    // Melder: erledigtes Ticket „doch nicht erledigt“ → zurück auf offen
    wiederOeffnen: (id: number, text?: string) =>
      request<{ data: Ticket }>(`/api/tickets/${id}/wieder-oeffnen`, { method: 'POST', body: JSON.stringify({ text }) }),
    // Zeitleiste (angelegt, Status, Zuweisung, Kommentare)
    verlauf: (id: number) => request<{ data: TicketVerlaufEintrag[] }>(`/api/tickets/${id}/verlauf`),
    // Kommentar-Verlauf
    kommentare: (id: number) => request<{ data: TicketKommentar[] }>(`/api/tickets/${id}/kommentare`),
    kommentar: (id: number, text: string) =>
      request<{ data: TicketKommentar }>(`/api/tickets/${id}/kommentare`, { method: 'POST', body: JSON.stringify({ text }) }),
    kommentarLoeschen: (id: number, kid: number) =>
      request<{ ok: boolean }>(`/api/tickets/${id}/kommentare/${kid}`, { method: 'DELETE' }),
    // Anhänge / Screenshots (R2)
    dateien: (id: number) => request<{ data: TicketDatei[] }>(`/api/tickets/${id}/dateien`),
    dateiUrl: (id: number, name: string) => `${API_BASE}/api/tickets/${id}/dateien/${encodeURIComponent(name)}`,
    dateiHochladen: async (id: number, datei: File): Promise<{ data: TicketDatei }> => {
      const fd = new FormData();
      fd.append('datei', datei);
      const res = await fetch(`${API_BASE}/api/tickets/${id}/dateien`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
        headers: { ...(import.meta.env.DEV && { 'X-Dev-User': 'hamdi@sickinger.de' }) },
      });
      if (!res.ok) {
        let body: unknown = null;
        try { body = await res.json(); } catch { /* ignore */ }
        throw new ApiError(res.status, body, `API ${res.status}: Datei-Upload`);
      }
      return res.json() as Promise<{ data: TicketDatei }>;
    },
    dateiLoeschen: (id: number, name: string) =>
      request<{ ok: boolean }>(`/api/tickets/${id}/dateien/${encodeURIComponent(name)}`, { method: 'DELETE' }),
  },
};

// Bereichs-Liste für Tickets — gruppiert nach "wer kümmert sich": Büro/Software
// vs. Produktion (echte Abteilungen). Melden-Dialog + Filter rendern optgroups.
export const TICKET_BEREICH_GRUPPEN: Array<{ gruppe: string; bereiche: string[] }> = [
  { gruppe: '🖥️ Büro & Software', bereiche: ['IT / EDV', 'Einkauf', 'Kalkulation / Angebot', 'Vertrieb', 'Terminplanung', 'Personal'] },
  { gruppe: '🏭 Produktion',        bereiche: ['Drückerei', 'Laser / Kant', 'Schlosserei', 'Laufrad-Schweißerei', 'Lackiererei', 'Montage', 'Versand', 'QS'] },
  { gruppe: 'Weitere',              bereiche: ['Sonstiges'] },
];
// Flache Liste (Abwärtskompatibilität / einfache Iteration)
export const TICKET_BEREICHE = TICKET_BEREICH_GRUPPEN.flatMap(g => g.bereiche);

// Ticket-Typ (Fehler/Wunsch/Frage) — Icon + Label je Wert.
export const TICKET_TYPEN: Record<Ticket['typ'], { label: string; icon: string }> = {
  fehler: { label: 'Fehler', icon: '🐞' },
  wunsch: { label: 'Wunsch', icon: '💡' },
  frage:  { label: 'Frage',  icon: '❓' },
};

export interface Ticket {
  id: number; titel: string; beschreibung: string | null; bereich: string | null;
  prio: 'normal' | 'wichtig' | 'dringend'; status: 'offen' | 'in_arbeit' | 'rueckfrage' | 'erledigt' | 'abgelehnt';
  typ: 'fehler' | 'wunsch' | 'frage'; komm_anzahl?: number;
  created_at: string; updated_at: string; erledigt_at: string | null;
  gemeldet_von: string | null; gemeldet_kurzel: string | null;
  zugewiesen_an: number | null; zugewiesen_name?: string | null; zugewiesen_kurzel?: string | null;
}

export interface TicketVerlaufEintrag {
  art: 'angelegt' | 'status' | 'zuweisung' | 'kommentar' | string; am: string; von: string | null; text: string | null;
  von_wert?: string | null; nach_wert?: string | null; ziel_name?: string | null;
}
export interface TicketKommentar {
  id: number; text: string; created_at: string;
  von?: string | null; von_kurzel?: string | null;
}
export interface TicketDatei {
  name: string; groesse: number; hochgeladen?: string;
  von?: string | null; content_type?: string;
}

export interface EinkaufAufgabe {
  id: number; titel: string; notiz: string | null; angefordert_von: string | null;
  prio: 'normal' | 'eilig'; status: 'offen' | 'bestellt' | 'erledigt';
  erledigt_at: string | null; created_at: string; erfasst_von: string | null;
  typ?: 'bestellung' | 'anfrage'; ansprechpartner?: string | null; bild_key?: string | null;
  storno_grund?: string | null; storniert_am?: string | null;
}
export interface EinkaufAufgabePapierkorb extends EinkaufAufgabe {
  deleted_at: string | null; storniert_von_name: string | null;
}

export interface SonderteilAnfrage {
  id: number;
  created_by_id: number;
  was: string;
  kunde_id: number | null;
  kunde_name?: string | null;
  dv_nummer: string | null;
  lieferant_id: number | null;
  lieferant_name?: string | null;
  angefragt_bei: string | null;
  angefragt_bei_email: string | null;
  ersteller_name?: string | null;
  preis: number | null;
  liefertermin: string | null;
  kommentar: string | null;
  status: 'offen' | 'bestellt';
  einkauf_aufgabe_id: number | null;
  bestellt_am: string | null;
  created_at: string;
  updated_at: string;
}

// === Kundeninfo (Termin-Radar + Sammellieferung) ===
export interface KundeStamm {
  id: number; name: string; active: number;
  contact: string; email: string; phone: string; notes: string;
  offene_teile: number;
}
export interface KundenInfoTeil {
  modul: string; id: number; bestellnr: string; dv_nummer: string | null;
  bezeichnung: string | null; stueck: number; lieferdatum: string; tage: number;
  kunde_id: number; kunde_name: string; kunde_email: string; kunde_contact: string;
  sb: string | null;
}

// === Einkauf-Dashboard ===
export interface EinkaufStatsItem {
  id: number; datum: string | null; sb: string | null; menge: string | null;
  form?: string | null; werkstoff: string | null; format: string | null; dicke: string | null;
  termin?: string | null; referenz: string | null; lieferanten_angefragt?: string | null;
  anfrage_datum?: string | null; lieferant?: string | null; liefertermin?: string | null;
  liefertermin_datum?: string | null;
  mahnungen?: number; letzte_mahnung?: string | null; kommentar?: string | null;
  ab_bestaetigt?: number; tage?: number; bestell_datum?: string | null; bestellnr?: string | null;
}
export interface EinkaufStats {
  kpi: {
    offen: number; offen_alt: number; unterwegs: number; ohne_ab: number;
    ueberfaellig: number; woche: number; monat_neu: number;
    offen_profilrohr?: number; offen_sonstiges?: number;
    dlz_anfrage?: number | null; dlz_lieferzeit?: number | null;
  };
  topWerkstoffe: { werkstoff: string; n: number }[];
  topLieferanten: { lieferant: string; n: number }[];
  trend: { monat: string; n: number }[];
  wartet: EinkaufStatsItem[];
  ueberfaellig: EinkaufStatsItem[];
  erwartet: EinkaufStatsItem[];
}
// === Dokumente je Blechbedarf (PDF in R2) ===
export interface BedarfDatei {
  name: string;
  groesse: number;
  hochgeladen: string;
  von: string | null;
  typ?: string | null;
}

// === Preisspiegel (Einkauf E2) ===
export interface SonstigesBild { name: string; groesse: number; hochgeladen: string; von: string | null }

export interface EinkaufAngebot {
  id: number;
  bedarf_id: number;
  lieferant: string;
  status: 'offen' | 'erhalten' | 'absage';
  preis: number | null;
  preis_einheit: string | null;
  liefertermin: string | null;
  angefragt_am: string | null;
  erhalten_am: string | null;
  notiz: string | null;
  created_at: string;
  updated_at: string;
}

export interface WerkstoffAnalyse {
  werkstoff: string;
  summe: { n_gesamt: number; avg_kg_12m: number | null } | null;
  jahre: { jahr: string; n: number; menge_sum: number | null; avg_kg: number | null; min_kg: number | null; max_kg: number | null }[];
  lieferanten: { lieferant: string; n: number; avg_kg: number | null; letzter: string | null }[];
  letzte: { id: number; datum: string | null; menge: string | null; form: string | null; format: string | null; dicke: string | null; lieferant: string | null; preis: number | null; preis_einheit: string | null; referenz: string | null }[];
}
export interface BedarfMatrixAchse { key: string; label: string; n: number; raw: string[] }
export interface BedarfMatrix {
  gesamt: number;
  dicken: BedarfMatrixAchse[];
  formate: BedarfMatrixAchse[];
  zellen: { dk: string; fk: string; n: number }[];
  kombis: { dk: string; fk: string; dicke: string; format: string; n: number; raw_dicke: string[]; raw_format: string[] }[];
  // Kennzahlen aus den Einzelkäufen (Preis nur aus kg-Preisen): gesamt, je Blechstärke, je Blechstärke × Format
  kpi?: { n_gesamt: number; n_12m: number; avg_kg_12m: number | null; avg_kg_vor: number | null; trend_pct: number | null;
          letzter: WerkstoffLetzter | null; haupt_lieferant: string | null; haupt_n: number };
  detail?: (WerkstoffGruppe & { formate: WerkstoffGruppe[] })[];
}
export interface WerkstoffLetzter { datum: string | null; preis: number | null; einheit: string | null; lieferant: string | null }
export interface WerkstoffGruppe {
  key: string; label: string; n: number; menge_sum: number | null;
  avg_kg: number | null; min_kg: number | null; max_kg: number | null;
  avg_kg_12m: number | null; avg_kg_vor: number | null; trend_pct: number | null;
  letzter: WerkstoffLetzter; haupt_lieferant: string | null; haupt_n: number;
}
export interface WerLiefertWas {
  id: number; produkt: string | null; lieferant: string | null;
  ansprechpartner: string | null; telefon: string | null; email: string | null; bemerkung: string | null;
}

export type EinkaufSonstigesKategorie =
  'loxpal' | 'sonstiges' | 'projekte' | 'schietinger' | 'versand' | 'wuerth_flex' | 'farbe' | 'schweissen';

export interface EinkaufSonstigesEintrag {
  id: number;
  kategorie: EinkaufSonstigesKategorie;
  datum: string | null;
  stueck: string | null;
  was: string;
  kg: number | null;
  kommentar: string | null;
  angefragt_bei: string | null;
  bestellt_bei: string | null;
  liefertermin: string | null;
  preis: string | null;
  einheit: string | null;
  ab: string | null;
  rechnungsnummer: string | null;
  referenz: string | null;
  geliefert_am?: string | null;     // Wareneingang (Datum)
  storno_grund?: string | null;
  storniert_am?: string | null;
  created_at: string;
  updated_at: string;
}
export interface EinkaufSonstigesPapierkorb extends EinkaufSonstigesEintrag {
  deleted_at: string | null; storniert_von_name: string | null;
}

export interface LieferantBewertung {
  lieferzeit_avg?: number | null;
  id: number; name: string;
  kaeufe: number; kaeufe_12m: number;
  termin_basis: number; puenktlich: number;
  verzug_avg: number | null;
  angefragt: number; absagen: number;
}

// === Lieferantenstamm (Einkauf) ===
export interface Lieferant {
  id: number;
  name: string;
  ansprechpartner: string | null;
  email: string | null;
  telefon: string | null;
  notiz: string | null;
  aktiv: number;
  kaeufe?: number;
  letzter_kauf?: string | null;
  created_at: string;
  updated_at: string;
}

// Angebot in der Listenansicht (Chips im Reiter „Angefragt“)
export interface ListenAngebot {
  id: number; bedarf_id: number; lieferant: string; status: 'offen' | 'erhalten' | 'absage';
  preis: number | null; preis_einheit: string | null; liefertermin: string | null; notiz: string | null;
}

// === Blechbedarf (Material-Beschaffung) ===
export interface Blechbedarf {
  mahnungen?: number; letzte_mahnung?: string | null;
  angebote_da?: number;
  angebote?: ListenAngebot[];
  id: number;
  datum: string | null;
  sb: string | null;
  menge: string | null;
  form: string | null;
  werkstoff: string | null;
  format: string | null;
  dicke: string | null;
  termin: string | null;
  referenz: string | null;
  lieferanten_angefragt: string | null;
  anfrage_datum: string | null;
  anfrage_notiz: string | null;
  bestell_datum: string | null;
  besteller: string | null;
  zeugnis: string | null;
  ab_bestaetigt: number;
  lieferant: string | null;
  liefertermin: string | null;
  liefertermin_datum: string | null;
  bedarfstyp?: 'anfrage' | 'bestellung';
  rechnungsnr?: string | null;
  rechnungsbetrag?: number | null;
  re_geprueft?: number;
  re_datum?: string | null;
  bestellt: number;
  preis: number | null;
  preis_einheit: string | null;
  wareneingang: string | null;
  menge_angekommen: string | null;
  bestellnr: string | null;
  kommentar: string | null;
  priority: 'normal' | 'eilig' | 'kritisch';
  fertig: number;
  nachbedarf_am?: string | null;
  nachbedarf_notiz?: string | null;
  artikel_id?: number | null;
  lieferanten_artikelnr?: string | null;
  storno_grund?: string | null;
  storniert_am?: string | null;
  storniert_von_id?: number | null;
  deleted_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Artikel {
  id: number;
  kategorie: string;
  bezeichnung: string;
  form: string | null;
  werkstoff: string | null;
  format: string | null;
  dicke: string | null;
  lieferant: string | null;
  lieferanten_artikelnr: string | null;
  einheit?: string | null;
  vpe?: string | null;
  ean?: string | null;
  warengruppe?: string | null;
  listenpreis?: number | null;
  zugriffe?: number | null;          // wie oft beim Lieferanten bestellt (Import-Statistik, z. B. Würth)
  gesamtmenge?: number | null;
  stat_von?: string | null;
  stat_bis?: string | null;
  quelle?: string | null;
  aktiv: number;
  created_at: string;
}

// === Sammelbestellung-Zettel (freie Positionen, DV = interne Bestellnummer) ===
export interface Sammelzettel {
  id: number; dv_nummer: string; bezeichnung: string; notiz: string | null;
  status: 'offen' | 'erledigt' | 'storniert';
  storno_grund: string | null; storniert_am: string | null;
  created_at: string; ersteller?: string | null;
  pos_gesamt?: number; pos_offen?: number; pos_rueckfrage?: number; pos_bestellt?: number; pos_geliefert?: number;
  lieferanten?: string | null;
}
export interface SammelzettelPos {
  id: number; zettel_id: number; menge: number; einheit: string | null; bezeichnung: string;
  artikel_id: number | null; lieferant: string | null; lieferanten_artikelnr: string | null;
  bemerkung: string | null; bedarf_id: number | null;
  status: 'offen' | 'bestellt' | 'geliefert' | 'storniert';
  bestellnr: string | null; bestell_datum: string | null; besteller: string | null; liefertermin: string | null;
  // Rückfrage an den Chef: null | 'wartet' | 'freigegeben' | 'abgelehnt'
  rueckfrage?: 'wartet' | 'freigegeben' | 'abgelehnt' | null;
  frage_text?: string | null; frage_von?: string | null; frage_am?: string | null;
  entscheid_von?: string | null; entscheid_am?: string | null; entscheid_text?: string | null;
}
export interface SammelzettelRueckfrage extends SammelzettelPos { dv_nummer: string; zettel_bezeichnung: string }
export interface SammelzettelNeu { dv_nummer: string; bezeichnung: string; notiz?: string }
export interface SammelzettelPosNeu {
  bezeichnung?: string; menge?: number | string; einheit?: string; artikel_id?: number | null;
  lieferant?: string; lieferanten_artikelnr?: string; bemerkung?: string;
}
export interface SammelzettelErgebnis {
  data: { lieferant: string; bestellnr: string; positionen: SammelzettelPos[] }[];
  anzahl: number; nicht_bestellt: number[];
}

// Sammelbestellung je DV: eine Gruppe = ein Lieferant mit seinen angekreuzten Positionen
export interface SammelGruppe { lieferant: string; ids: number[]; bestellnr?: string; liefertermin?: string }
export interface SammelErgebnis {
  data: { lieferant: string; bestellnr: string | null; positionen: Blechbedarf[] }[];
  anzahl: number;
  nicht_bestellt: number[];
}

export interface DvOffen { referenz: string; offen: number; mit_lieferant: number; neuestes: string | null; aeltestes: string | null; lieferanten: string | null }

export interface BedarfPapierkorb extends Blechbedarf {
  storniert_von_name: string | null;
}

export interface BedarfDvUebersicht {
  data: Pick<Blechbedarf, 'id' | 'datum' | 'sb' | 'menge' | 'form' | 'werkstoff' | 'format' | 'dicke' | 'referenz' | 'lieferant' |
    'lieferanten_angefragt' | 'anfrage_datum' | 'bestellt' | 'bestell_datum' | 'besteller' | 'bestellnr' | 'liefertermin' |
    'preis' | 'preis_einheit' | 'fertig' | 'wareneingang' | 'kommentar' | 'priority' | 'lieferanten_artikelnr'>[];
  summary: { gesamt: number; offen: number; angefragt: number; bestellt: number; fertig: number; storniert: number; lieferanten: string[] };
}

// === A3 Durchlaufplanung Types ===
export interface PriorisierterAuftrag {
  modul: 'drueckteile' | 'laufraeder' | 'baugruppen' | 'montage';
  id: number;
  bestellnr: string;
  kunde_name: string | null;
  bezeichnung: string | null;
  lieferdatum: string | null;
  priority: 'normal' | 'eilig' | 'kritisch';
  score: number;
  bucket: '🔥' | '⏰' | '✅';
  gesamtzeit: number | null;
  days_to_delivery: number | null;
  first_active_at: string | null;
}

// === A2 Kapazitäten Types ===
export interface KapazitaetWoche {
  kw: number;
  soll: number;
  ist: number;
  last_percent: number | null;
  has_soll: boolean;
  is_default?: boolean;
  notes: string | null;
}
export interface KapazitaetAbteilungRow {
  abteilung_id: number;
  abteilung_name: string;
  abteilung_farbe: string;
  default_soll?: number;
  engpass_anzahl?: number | null;
  engpass_typ?: string | null;
  wochen: KapazitaetWoche[];
}
export interface KapazitaetOverview {
  jahr: number;
  kw_start: number;
  kw_count: number;
  weeks: number[];
  data: KapazitaetAbteilungRow[];
}
export interface KapazitaetZelle {
  abteilung_id: number;
  jahr: number;
  kw: number;
  stunden_soll: number;
  default_soll?: number;
  engpass_anzahl?: number | null;
  engpass_typ?: string | null;
  stunden_pro_einheit?: number | null;
  effizienz_faktor?: number | null;
  has_manual_soll?: boolean;
  ist_stunden: number;
  last_percent: number | null;
  notes: string | null;
  updated_at: string | null;
  updated_by_id: number | null;
  updated_by_name: string | null;
  has_soll: boolean;
}
export interface KapazitaetOverload {
  abteilung_id: number;
  name: string;
  farbe: string;
  stunden_soll: number;
  ist_stunden: number;
  last_percent: number | null;
}

// === A1 Durchlaufzeit Types ===
export interface DurchlaufzeitMetric {
  count: number;
  avg_kalender: number;
  p50_kalender: number;
  p90_kalender: number;
  avg_aktiv: number;
}
export interface DurchlaufzeitModul {
  modul: string;
  all: DurchlaufzeitMetric;
  clean: DurchlaufzeitMetric;
  cleanup_count: number;
}
export interface DurchlaufzeitOverview {
  days: number;
  total_records: number;
  clean_records: number;
  data_quality: 'keine_sauberen_daten' | 'gering' | 'mittel' | 'gut';
  note: string | null;
  modules: DurchlaufzeitModul[];
}
export interface DurchlaufzeitAuftrag {
  id: number;
  bestellnr: string;
  lieferdatum: string;
  fertig_datum: string;
  created_at: string;
  first_active_at: string | null;
  dl_tage_kalender: number | null;
  dl_tage_aktiv: number | null;
  is_cleanup_artifact: number;
  kunde_name: string | null;
}
export interface TopLauferAuftrag {
  modul: string;
  id: number;
  bestellnr: string;
  lieferdatum: string;
  first_active_at: string;
  tage_aktiv: number;
}

export interface DashboardLiveResponse {
  ts: string;
  feed: { data: FeedEvent[] };
  active: { data: ArbeitSession[] };
  heatmap: { data: HeatmapEntry[] };
  qs: { count: ProblemCount; list: ProblemMeldung[] };
}

export interface SachbearbeiterWorkload {
  user_id: number;
  name: string;
  kurzel: string | null;
  offen: number;
  ueberfaellig: number;
  stunden_offen: number;
  stunden_ueberfaellig: number;
}

export interface WochenRecap {
  name: string;
  kw: number;
  team: { thisWeek: number; lastWeek: number; delta: number };
  mine: { thisWeek: number; lastWeek: number; delta: number };
}

export interface DashboardMe {
  user: { id: number; name: string; role: string; kurzel: string | null };
  offenGesamt: number;
  offenUeberfaellig: number;
  sachbearbeiterWorkload: SachbearbeiterWorkload[];
  aktuelleWoche: {
    fertig: number;
    offen: number;
    stunden: number;
    reststundenBisFreitag: number;
    ueberfaelligeStunden: number;
  };
  eilig: { total: number; ueberfaellig: number };
  neu: DashboardItem[];
  termine: {
    counts: { ueberfaellig: number; kritisch: number; bald: number; naechste_woche: number };
    ueberfaellig: DashboardItem[];
    kritisch:     DashboardItem[];
    bald:         DashboardItem[];
  };
}

export interface DashboardItem {
  id: number;
  bestellnr: string;
  dv_nummer: string | null;
  position: string | null;
  stueck: number;
  gesamtzeit: number;
  lieferdatum: string;
  kw: number;
  priority: 'normal' | 'eilig' | 'kritisch';
  days_to_delivery: number;
  kunde_name: string | null;
  sachbearbeiter_name: string | null;
  stand_id: number | null;
  stand_wert: string | null;
  stand_farbe: string | null;
  updated_at: string;
  created_at?: string;
  masse_bezeichnung?: string | null;
  modul?: 'drueckteile' | 'laufraeder' | 'baugruppen' | 'montage' | 'laser_kant';
}

export interface BedarfHistorie {
  id: number; record_id: number; action: 'INSERT' | 'UPDATE' | 'DELETE' | 'RESTORE';
  old_data: string | null; new_data: string | null; changed_at: string; user_name: string | null;
}
